/**
 * Utility functions for capturing room screenshots/thumbnails
 */

import html2canvas from 'html2canvas';

/**
 * Current screenshot version - increment to force re-capture of all room screenshots
 * This allows the system to regenerate thumbnails when screenshot logic changes
 * (e.g., adding YouTube thumbnail overlays, changing composition, etc.)
 */
export const SCREENSHOT_VERSION = 1;

/**
 * Extract YouTube video ID from URL
 */
export function extractYouTubeVideoId(url) {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

/**
 * Get YouTube thumbnail URL for a video
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - 'mqdefault', 'hqdefault', 'maxresdefault' (default: 'hqdefault')
 */
export function getYouTubeThumbnailUrl(videoId, quality = 'hqdefault') {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Capture a screenshot of the Three.js canvas
 * @param {THREE.WebGLRenderer} gl - The Three.js renderer
 * @param {Object} options - Screenshot options
 * @param {number} options.width - Desired width (default: 512)
 * @param {number} options.height - Desired height (default: 512)
 * @param {string} options.format - Image format: 'image/png' or 'image/jpeg' (default: 'image/jpeg')
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<string>} Base64 data URL of the screenshot
 */
export const captureRoomScreenshot = async (gl, options = {}) => {
  const {
    width = 512,
    height = 512,
    format = 'image/jpeg',
    quality = 0.8
  } = options;

  try {
    // Get the canvas from the renderer
    const canvas = gl.domElement;

    // Create a temporary canvas for resizing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const ctx = tempCanvas.getContext('2d');

    // Draw the original canvas onto the temp canvas (resizes it)
    ctx.drawImage(canvas, 0, 0, width, height);

    // Convert to data URL
    const dataUrl = tempCanvas.toDataURL(format, quality);

    return dataUrl;
  } catch (error) {
    console.error('Failed to capture room screenshot:', error);
    throw error;
  }
};

/**
 * Capture screenshot using html2canvas for DOM elements (iframes won't show video content
 * due to CORS, but we capture the container)
 * @param {HTMLElement} element - The DOM element to capture
 * @param {Object} options - Screenshot options
 * @returns {Promise<string>} Base64 data URL of the screenshot
 */
export const captureDomScreenshot = async (element, options = {}) => {
  const {
    width = 512,
    height = 512,
    format = 'image/jpeg',
    quality = 0.85,
    backgroundColor = '#ffffff'
  } = options;

  try {
    const canvas = await html2canvas(element, {
      backgroundColor,
      scale: 1,
      useCORS: true,
      allowTaint: false,
      logging: false,
      width,
      height
    });
    
    return canvas.toDataURL(format, quality);
  } catch (error) {
    console.error('Failed to capture DOM screenshot:', error);
    throw error;
  }
};

/**
 * Get YouTube video positions from decorations for overlaying thumbnails
 * @param {Array} decorations - Room decorations
 * @returns {Array} Array of {videoId, thumbnailUrl, position: {x, y}} objects
 */
export const getYouTubeDecorationsWithThumbnails = (decorations) => {
  if (!decorations || !Array.isArray(decorations)) return [];
  
  return decorations
    .filter(d => d.type === 'youtube' && d.option)
    .map(d => {
      const videoId = extractYouTubeVideoId(d.option);
      if (!videoId) return null;
      return {
        videoId,
        thumbnailUrl: getYouTubeThumbnailUrl(videoId, 'hqdefault'),
        position: d.position || { x: 0, y: 0 },
        width: d.width || 10,
        height: d.height || 10
      };
    })
    .filter(Boolean);
};

/**
 * Composite YouTube thumbnails onto the captured screenshot
 * @param {string} canvasDataUrl - Base64 data URL from canvas capture
 * @param {Array} youtubeDecorations - YouTube decoration info with thumbnail URLs
 * @param {Object} camera - Camera object with position info
 * @param {Object} options - Options like output size
 * @returns {Promise<string>} Composite data URL
 */
export const compositeYouTubeThumbnails = async (canvasDataUrl, youtubeDecorations, camera, options = {}) => {
  if (!youtubeDecorations || youtubeDecorations.length === 0) {
    return canvasDataUrl;
  }
  
  return new Promise((resolve, reject) => {
    try {
      // Load the canvas image to get actual dimensions
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Draw the original canvas capture
        ctx.drawImage(img, 0, 0);
        
        // Overlay YouTube thumbnails at bottom-right corner
        const thumbSize = Math.min(60, width / 5);
        const padding = 8;
        const thumbsPerRow = Math.ceil(Math.sqrt(youtubeDecorations.length));
        
        youtubeDecorations.forEach((yt, index) => {
          const row = Math.floor(index / thumbsPerRow);
          const col = index % thumbsPerRow;
          const x = padding + col * (thumbSize + padding);
          const y = height - padding - row * (thumbSize + padding) - thumbSize;
          
          // Draw thumbnail
          const thumbImg = new Image();
          thumbImg.onload = () => {
            // Draw rounded rectangle background
            ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            ctx.beginPath();
            ctx.roundRect(x, y, thumbSize, thumbSize, 4);
            ctx.fill();
            
            // Draw thumbnail
            ctx.drawImage(thumbImg, x + 2, y + 2, thumbSize - 4, thumbSize - 4);
            
            // Draw play button overlay
            ctx.fillStyle = 'rgba(255, 0, 0, 0.9)';
            ctx.beginPath();
            const centerX = x + thumbSize / 2;
            const centerY = y + thumbSize / 2;
            const btnSize = thumbSize * 0.25;
            ctx.arc(centerX, centerY, btnSize, 0, Math.PI * 2);
            ctx.fill();
            
            // Draw play triangle
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.moveTo(centerX - btnSize * 0.3, centerY - btnSize * 0.5);
            ctx.lineTo(centerX - btnSize * 0.3, centerY + btnSize * 0.5);
            ctx.lineTo(centerX + btnSize * 0.5, centerY);
            ctx.closePath();
            ctx.fill();
          };
          thumbImg.src = yt.thumbnailUrl;
        });
        
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => resolve(canvasDataUrl);
      img.src = canvasDataUrl;
    } catch (error) {
      console.error('Failed to composite thumbnails:', error);
      resolve(canvasDataUrl); // Fallback to original
    }
  });
};

/**
 * Upload screenshot to server and update room record
 * @param {string} dataUrl - Base64 data URL of the screenshot
 * @param {string} roomId - Room ID
 * @param {Object} userInfo - User authentication info
 * @returns {Promise<{screenshotS3Key: string, screenshotGeneratedAt: string}>} Screenshot metadata
 */
export const uploadRoomScreenshot = async (dataUrl, roomId, userInfo) => {
  try {
    const { roomApi } = await import('../services/api');

    // Extract base64 data from data URL
    const base64Data = dataUrl.replace(/^data:image\/\w+;base64,/, '');
    const contentTypeMatch = dataUrl.match(/^data:(\w+);base64,/);
    const contentType = contentTypeMatch ? `image/${contentTypeMatch[1] === 'jpeg' ? 'jpeg' : contentTypeMatch[1]}` : 'image/jpeg';

    // Call the screenshot upload API via roomApi
    const result = await roomApi.uploadScreenshot(roomId, base64Data, contentType, userInfo);
    console.log('Screenshot uploaded:', result);

    return result;
  } catch (error) {
    console.error('Failed to upload room screenshot:', error);
    throw error;
  }
};

/**
 * Capture and save room screenshot
 * This is the main function to call from the editor
 * @param {THREE.WebGLRenderer} gl - The Three.js renderer
 * @param {string} roomId - Room ID
 * @param {Object} userInfo - User authentication info
 * @param {Object} options - Options
 * @param {boolean} options.silent - If true, suppress console logs (default: false)
 * @param {Function} options.onComplete - Optional callback when screenshot is saved successfully
 * @param {Array} options.decorations - Room decorations to extract YouTube thumbnails from
 * @returns {Promise<{screenshotS3Key: string, screenshotGeneratedAt: string}>} Screenshot metadata
 */
export const captureAndSaveRoomThumbnail = async (gl, roomId, userInfo, options = {}) => {
  const { silent = false, onComplete, decorations = [] } = options;
  
  try {
    // Wait for next frame to ensure 3D scene has rendered before capturing
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Capture screenshot at OG image resolution
    let dataUrl = await captureRoomScreenshot(gl, {
      width: 1200,
      height: 630,
      quality: 0.92
    });
    
    // If room has YouTube decorations, overlay thumbnails
    if (decorations.length > 0) {
      const youtubeDecorations = getYouTubeDecorationsWithThumbnails(decorations);
      if (youtubeDecorations.length > 0) {
        dataUrl = await compositeYouTubeThumbnails(dataUrl, youtubeDecorations, null);
      }
    }

    // Upload to server and update room record
    const screenshotData = await uploadRoomScreenshot(dataUrl, roomId, userInfo);

    if (!silent) {
      console.log('Room screenshot saved:', screenshotData);
    }
    
    // Dispatch event to notify other components (e.g., to invalidate caches)
    window.dispatchEvent(new CustomEvent('screenshotCaptured', { 
      detail: { roomId, screenshotData } 
    }));
    
    if (onComplete) {
      onComplete(screenshotData);
    }
    
    return screenshotData;
  } catch (error) {
    if (!silent) {
      console.error('Failed to capture and save room screenshot:', error);
    }
    throw error;
  }
};
